import type { ReactNode } from "react";
import { Hash } from "../atoms/Hash";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { CopyButton } from "../molecules/CopyButton";

export interface ProofStepListProps {
	leafHash: string;
	proof: string[];
	merkleRoot: string;
	leafIndex: number;
	txHash?: string | null;
	chainName?: string | null;
	polygonscanUrl?: string | null;
	className?: string;
}

function ProofStepList({
	leafHash,
	proof,
	merkleRoot,
	leafIndex,
	chainName,
	txHash,
	className,
}: ProofStepListProps): ReactNode {
	return (
		<div
			className={cn("flex flex-col gap-3", className)}
			aria-label="Proof steps"
		>
			<Heading size="sm">Proof Steps</Heading>
			<ol className="flex flex-col gap-2" aria-label="Numbered proof steps">
				<li className="flex items-center gap-2 rounded-md border border-border p-2">
					<span className="shrink-0 text-xs font-medium text-mutedForeground w-16">
						Leaf [{leafIndex}]
					</span>
					<Hash value={leafHash} />
					<CopyButton value={leafHash} />
				</li>
				{proof.map((sibling, idx) => (
					<li
						key={sibling}
						className="flex items-center gap-2 rounded-md border border-border p-2"
					>
						<span className="shrink-0 text-xs font-medium text-mutedForeground w-16">
							Step {idx + 1}
						</span>
						<Hash value={sibling} length={4} />
						<CopyButton value={sibling} />
					</li>
				))}
				<li className="flex items-center gap-2 rounded-md bg-muted border border-border p-2">
					<span className="shrink-0 text-xs font-medium text-mutedForeground w-16">
						Root
					</span>
					<Hash value={merkleRoot} />
					<CopyButton value={merkleRoot} />
				</li>
			</ol>
			{(chainName || txHash) && (
				<div className="text-xs text-mutedForeground">
					{chainName && <Text size="xs">Chain: {chainName}</Text>}
					{txHash && (
						<div className="flex items-center gap-1">
							<Text size="xs" as="span">
								Tx:
							</Text>
							<Hash value={txHash} length={4} />
						</div>
					)}
				</div>
			)}
		</div>
	);
}
ProofStepList.displayName = "ProofStepList";

export default ProofStepList;
export { ProofStepList };
